import { isEmpty } from 'lodash';
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { ReportStudioElementMetadataFactory } from '@/modules/ta/reportstudio/factories/ReportStudioElementMetadataFactory';
import { WidgetFactory } from '@/modules/ta/widget/factories/WidgetFactory';
import {
  ReportElementType,
  ReportElementShapeTypes,
  ReportElementMacroType,
} from '@/modules/ta/reportstudio/reportstudio.constants';
import { DrawOption, WidgetType } from '@/modules/ta/widget/widget.constants';

export class ReportStudioElement extends BaseEntityModel {
  /**
   * @var {string}
   */
  id;

  /**
   * @var {string}
   */
  widget_id;

  /**
   * @var {?string}
   */
  page_id;

  /**
   * @var {?string}
   */
  report_id;

  /**
   * @var {string}
   */
  type;

  /**
   * @var {number}
   */
  width;

  /**
   * @var {number}
   */
  height;

  /**
   * @var {boolean}
   */
  show_on_every_page;

  /**
   * @var {boolean}
   */
  snap_to_grid;

  /**
   * @var {ReportStudioElementMetadata}
   */
  metadata;

  /**
   * @var {datetime}
   */
  created_at;

  /**
   * @var {datetime}
   */
  updated_at;

  /**
   * @var {boolean}
   */
  is_deleted;

  /**
   * @var {number}
   */
  x_position;

  /**
   * @var {number}
   */
  y_position;

  /**
   * @var {number}
   */
  z_index;

  /**
   * This is a front-end only property to view/hide element.
   * @var {boolean}
   */
  is_hidden;

  /**
   * @var {boolean}
   */
  widget_needs_update;

  constructor(model = {}) {
    super(model);

    this.id = model.id;
    this.widget_id = model.widget_id;
    this.page_id = model.page_id;
    this.report_id = model.report_id;
    this.type = model.type;
    this.width = model.width;
    this.height = model.height;
    this.widget = model.widget ? WidgetFactory.build(model.widget) : null;
    this.show_on_every_page = model.show_on_every_page;
    this.snap_to_grid = model.snap_to_grid;
    this.metadata = ReportStudioElementMetadataFactory.build(model) || {};
    this.created_at = model.created_at;
    this.updated_at = model.updated_at;
    this.is_deleted = model.is_deleted;
    this.x_position = model.x_position;
    this.y_position = model.y_position;
    this.z_index = model.z_index;
    this.is_hidden = model.is_hidden ?? false;
    this.widget_needs_update = model.widget_needs_update;
  }

  /**
   *
   * @returns {boolean}
   */
  isTypeWidget() {
    return this.type === ReportElementType.WIDGET;
  }

  /**
   *
   * @returns {boolean}
   */
  isTypeExecutiveSummary() {
    return this.isTypeWidget() && this.widget.type === WidgetType.TYPE_EXECUTIVESUMMARY;
  }

  /**
   *
   * @returns {boolean}
   */
  isTypeShape() {
    return this.type === ReportElementType.SHAPE;
  }

  /**
   *
   * @returns {*|boolean}
   */
  isShapeSquare() {
    return this.metadata.shape_type && this.metadata.shape_type === ReportElementShapeTypes.SQUARE;
  }

  /**
   *
   * @returns {boolean}
   */
  isTypeText() {
    return this.type === ReportElementType.TEXT;
  }

  /**
   *
   * @returns {boolean}
   */
  isTypeImage() {
    return this.type === ReportElementType.IMAGE;
  }

  /**
   *
   * @returns {boolean|boolean}
   */
  isTypeMacroText() {
    return (
      this.type === ReportElementType.TEXT &&
      [
        ReportElementMacroType.DATE_RANGE,
        ReportElementMacroType.RECIPIENT_NAME,
        ReportElementMacroType.CREATED_ON,
        ReportElementMacroType.PAGE_NUMBER,
        ReportElementMacroType.PAGE_TITLE,
      ].includes(this.metadata.macro_type)
    );
  }

  isTypeMacroImage() {
    return (
      this.type === ReportElementType.IMAGE &&
      [ReportElementMacroType.COMPANY_LOGO].includes(this.metadata.macro_type)
    );
  }

  isTypeIcon() {
    return this.type === ReportElementType.ICON;
  }

  isWidgetUsingSampleData() {
    return this.isTypeWidget() && this.widget.metadata.draw_options[DrawOption.FORCE_SAMPLE_DATA];
  }

  hasWidgetFilters() {
    return (
      this.isTypeWidget() &&
      this.widget?.metadata?.dynamic &&
      !isEmpty(this.widget.metadata.dynamic.filters)
    );
  }
}
